/**
 * Postvak In — types, labels, badges, DTOs en Zod-schemas.
 *
 * Spec: docs/specs/postvak-in-module.md §4.
 *
 * Inbox-routing:
 *   - persoonlijk → userId gevuld, functie null
 *   - functie-inbox → functie gevuld, userId null
 *
 * Precies één van de twee is altijd gevuld (DB-CHECK + Zod-discriminator
 * in createInboxItem-service). Spec §3.1.
 */

import { z } from 'zod'
import { queryBoolean } from './zod-query'
import { idParam } from './id-validators'

// ─── Enums (matching Prisma enums as readonly string-tuples) ────────────────

export const INBOX_FUNCTIE = [
  'HR',
  'TEAMLEIDER',
  'PLANNER',
  'ICT',
  'RECRUITMENT',
  'SALARIS',
  'MANAGEMENT',
  'ADMINISTRATIE',
  'KWALITEIT',
  'VEILIGHEID',
] as const
export type InboxFunctie = (typeof INBOX_FUNCTIE)[number]

export const INBOX_ITEM_TYPE = ['NOTIFICATIE', 'ACTIE', 'WORKFLOW_STAP'] as const
export type InboxItemType = (typeof INBOX_ITEM_TYPE)[number]

export const INBOX_ITEM_STATUS = [
  'NIEUW',
  'GELEZEN',
  'OPGEPAKT',
  'AFGEHANDELD',
  'VERVALLEN',
] as const
export type InboxItemStatus = (typeof INBOX_ITEM_STATUS)[number]

// Ronde 4 W7 (spec ronde-4/07 §1.10) — BLOCKING toegevoegd (boven URGENT):
// blokkeert verdere actie, vereist onmiddellijke aandacht.
export const INBOX_ITEM_PRIORITEIT = ['LAAG', 'NORMAAL', 'HOOG', 'URGENT', 'BLOCKING'] as const
export type InboxItemPrioriteit = (typeof INBOX_ITEM_PRIORITEIT)[number]

// Kantoor interne bon (S3) — subtype-discriminator voor inbox-items die via
// `eventTypeKey` worden gerouteerd (InboxItem heeft géén eigen subType-kolom).
export const INBOX_ITEM_SUBTYPE = ['KANTOOR_GOEDKEURING'] as const
export type InboxItemSubType = (typeof INBOX_ITEM_SUBTYPE)[number]

// Postvak-in mappen (S5 §2.1) — organisatie-laag los van status. Een item heeft
// altijd precies één map; nieuwe items komen binnen in OPEN. AFGEHANDELD-items
// schuiven automatisch naar AFGEROND; VERWIJDERD wordt na 30 dagen hard-deleted.
export const INBOX_ITEM_MAP = ['OPEN', 'GEPLAND', 'AFGEROND', 'VERWIJDERD'] as const
export type InboxItemMap = (typeof INBOX_ITEM_MAP)[number]

// FO-151 — de twee vaste postvak-secties: "Vraagt actie" (type ACTIE +
// WORKFLOW_STAP) bovenaan en "Ter info" (type NOTIFICATIE) eronder. De
// lijst-route vertaalt de sectie server-side naar het type-filter (+ open
// status in de default-view) zodat beide secties efficiënt gepagineerd laden.
export const INBOX_SECTIE = ['actie', 'info'] as const
export type InboxSectie = (typeof INBOX_SECTIE)[number]

/** De InboxItemType-set per sectie (server- én client-side dezelfde bron). */
export const INBOX_SECTIE_TYPES: Record<InboxSectie, readonly InboxItemType[]> = {
  actie: ['ACTIE', 'WORKFLOW_STAP'],
  info: ['NOTIFICATIE'],
}

// ─── Labels (NL-talig voor UI) ──────────────────────────────────────────────

export const INBOX_FUNCTIE_LABELS: Record<InboxFunctie, string> = {
  HR:            'HR',
  TEAMLEIDER:    'Teamleider',
  PLANNER:       'Planner',
  ICT:           'ICT',
  RECRUITMENT:   'Recruitment',
  SALARIS:       'Salarisadministratie',
  MANAGEMENT:    'Management',
  ADMINISTRATIE: 'Administratie',
  KWALITEIT:     'Kwaliteit',
  VEILIGHEID:    'Veiligheid / KAM',
}

export const INBOX_ITEM_TYPE_LABELS: Record<InboxItemType, string> = {
  NOTIFICATIE:   'Melding',
  ACTIE:         'Actie vereist',
  WORKFLOW_STAP: 'Workflow-stap',
}

export const INBOX_ITEM_STATUS_LABELS: Record<InboxItemStatus, string> = {
  NIEUW:       'Nieuw',
  GELEZEN:     'Gelezen',
  OPGEPAKT:    'Opgepakt',
  AFGEHANDELD: 'Afgehandeld',
  VERVALLEN:   'Vervallen',
}

export const INBOX_ITEM_PRIORITEIT_LABELS: Record<InboxItemPrioriteit, string> = {
  LAAG:     'Laag',
  NORMAAL:  'Normaal',
  HOOG:     'Hoog',
  URGENT:   'Urgent',
  BLOCKING: 'Blokkerend',
}

export const INBOX_ITEM_MAP_LABELS: Record<InboxItemMap, string> = {
  OPEN:       'Open',
  GEPLAND:    'Gepland',
  AFGEROND:   'Afgerond',
  VERWIJDERD: 'Verwijderd',
}

// ─── Badge-varianten (shadcn-stijl) ─────────────────────────────────────────

export const INBOX_PRIORITEIT_BADGE: Record<
  InboxItemPrioriteit,
  'secondary' | 'info' | 'warning' | 'danger'
> = {
  LAAG:     'secondary',
  NORMAAL:  'info',
  HOOG:     'warning',
  URGENT:   'danger',
  BLOCKING: 'danger',
}

export const INBOX_STATUS_BADGE: Record<
  InboxItemStatus,
  'info' | 'warning' | 'success' | 'secondary' | 'danger'
> = {
  NIEUW:       'info',
  GELEZEN:     'secondary',
  OPGEPAKT:    'warning',
  AFGEHANDELD: 'success',
  VERVALLEN:   'secondary',
}

// Lucide icon-naam per functie. UI mapt deze string-keys naar concrete
// Lucide-componenten (zie inbox-counts-sidebar in PR-A3).
export const INBOX_FUNCTIE_ICON: Record<InboxFunctie, string> = {
  HR:            'Users',
  TEAMLEIDER:    'Shield',
  PLANNER:       'CalendarDays',
  ICT:           'Monitor',
  RECRUITMENT:   'UserPlus',
  SALARIS:       'Coins',
  MANAGEMENT:    'Briefcase',
  ADMINISTRATIE: 'FileText',
  KWALITEIT:     'Award',
  VEILIGHEID:    'HardHat',
}

// ─── DTOs ───────────────────────────────────────────────────────────────────

export interface InboxItemListRowDTO {
  id: string
  type: InboxItemType
  status: InboxItemStatus
  prioriteit: InboxItemPrioriteit
  titel: string
  inhoud: string
  link: string | null
  bronModule: string | null
  /** Event-type-discriminator (bv. 'KANTOOR_GOEDKEURING'); null als niet gezet. */
  eventTypeKey: string | null
  /** Bron-entiteit-id (bv. werkbon-id bij KANTOOR_GOEDKEURING); null als niet gezet. */
  bronEntiteitId: string | null
  /** Organisatie-map (S5 §2.1) — OPEN/GEPLAND/AFGEROND/VERWIJDERD, los van status. */
  map: InboxItemMap
  // Routing — precies één van userId/functie is gevuld.
  userId: string | null
  functie: InboxFunctie | null
  // FO-371 fix 1 — gecombineerde functie-chips voor de "alles"-tab. Voor een
  // enkel functie-item is dit `[functie]`; voor een persoonlijk item `[]`; voor
  // een gededupeerde rij (meerdere functie-inboxen met hetzelfde event + bron,
  // bv. Declaraties · HR + Salarisadministratie) alle betrokken functies. De
  // per-functie-tabs tonen nog steeds hun eigen enkele `functie`.
  functies: InboxFunctie[]
  // Workflow.
  workflowStap: number | null
  workflowTotaal: number | null
  deadlineOp: string | null
  // Claim.
  opgepaktDoor: { id: string; naam: string } | null
  opgepaktOp: string | null
  // FO-371 fix 1 — de functie-inbox waarlangs de claim is opgepakt, voor het
  // "opgepakt door {naam} ({functie})"-label in de gekoppelde functie-inbox.
  opgepaktViaFunctie: InboxFunctie | null
  // Meta.
  isGelezen: boolean   // Voor huidige user (via InboxLeesStatus of gelezenOp).
  // FO-107 — persoonlijke "ster" van de huidige user (aandacht-sectie bovenaan).
  isGemarkeerd: boolean
  createdAt: string
}

export interface InboxItemDetailDTO extends InboxItemListRowDTO {
  afgehandeldDoor: { id: string; naam: string } | null
  afgehandeldOp: string | null
  resultaat: string | null
  /**
   * batch-67 meldingen-ontvangers (FO-500 §C7) - waarom de kijker dit item ziet,
   * als zin achter "Waarom: ". `null` als er geen spoor is (een item van voor
   * batch-67, of een werkopdracht die niet uit een melding komt).
   */
  waarom: string | null
  /**
   * batch-67 (FO-500 §B6) - kan de kijker "Ik hoor dit niet te krijgen" gebruiken?
   * Bij een item uit een melding (persoonlijk of in een gedeeld postvak).
   */
  nietVoorMijMogelijk: boolean
  workflowItems: Array<{
    id: string
    stap: number
    titel: string
    status: InboxItemStatus
  }>
}

export interface InboxCountsDTO {
  totaalOngelezen: number
  persoonlijk: number
  perFunctie: Record<InboxFunctie, number>
  /** Aantal items (niet-verwijderd uit query-oogpunt) per organisatie-map (S5 §2.3). */
  perMap: Record<InboxItemMap, number>
}

export interface InboxListMeta {
  total: number
  page: number
  limit: number
  hasMore: boolean
}

/**
 * Spec 19 §5.2.C — prefill-context voor de "Inplannen"-deeplink. De backend
 * resolvet de bron-entiteit van een InboxItem (werkbon / planning / klant-
 * aanvraag) naar de klant + locatie waarmee het planbord de werkbon-create-
 * modal vooringevuld opent. `null`-velden betekenen "niet resolvbaar voor dit
 * bron-type" — het planbord opent de modal dan zonder die prefill, maar mét de
 * `bronInboxId`-koppeling zodat het item na inplannen alsnog wordt afgehandeld.
 */
export interface InboxInplannenContextDTO {
  /** Klant-id voor de werkbon-create-modal (null = niet te herleiden). */
  clientId: string | null
  /** Klantnaam voor de chip vóór context-load (null als clientId null is). */
  clientName: string | null
  /** Locatie-omschrijving van de bron-werkbon, indien bekend. */
  locatie: string | null
}

export interface UserInboxFunctieDTO {
  id: string
  userId: string
  userName: string
  functie: InboxFunctie
}

// ─── Zod-schemas (filter/claim/afhandelen/admin) ────────────────────────────

export const inboxItemFilterSchema = z.object({
  tab: z.enum(['alles', 'persoonlijk', 'functie']).default('alles'),
  functie: z.enum(INBOX_FUNCTIE).optional(),
  // FO-151 — vaste sectie: 'actie' (ACTIE + WORKFLOW_STAP) of 'info'
  // (NOTIFICATIE). Gaat vóór het losse type-filter; in de default-view filtert
  // de sectie ook server-side op open status.
  sectie: z.enum(INBOX_SECTIE).optional(),
  type: z.enum(INBOX_ITEM_TYPE).optional(),
  status: z.enum(INBOX_ITEM_STATUS).optional(),
  prioriteit: z.enum(INBOX_ITEM_PRIORITEIT).optional(),
  map: z.enum(INBOX_ITEM_MAP).optional(),
  bronModule: z.string().optional(),
  zoek: z.string().optional(),
  // FO-107 — persoonlijk gemarkeerde items van de huidige user.
  gemarkeerd: queryBoolean().optional(),
  // FO-107 — "Vraagt nu aandacht"-snelfilter: open ACTIE OF deadline binnen de
  // drempel OF persoonlijk gemarkeerd.
  aandacht: queryBoolean().optional(),
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(10).max(100).default(25),
})

export const inboxItemClaimSchema = z.object({
  actie: z.enum(['oppakken', 'loslaten']),
})

export const inboxItemAfhandelSchema = z.object({
  resultaat: z.string().max(500).optional(),
})

// Spec 19 §5.2.D — doorsturen naar functie OF persoon (precies één doel).
//
// Persoon kan op twee manieren worden aangeleverd:
//   • `userId`     — direct een interne User-id (gebruikt door programmatische
//                    callers / bestaande integraties).
//   • `employeeId` — een Employee-id zoals de EmployeePicker die levert; de
//                    API resolvet die server-side naar de gekoppelde User-id
//                    (Employee.userId, 1:1) vóór het doorsturen. De UI heeft
//                    geen User-id van een medewerker bij de hand, alleen de
//                    Employee-id uit de picker — daarom dit alternatief.
// Precies één van functie / userId / employeeId moet gevuld zijn.
export const inboxItemDoorsturenSchema = z
  .object({
    functie: z.enum(INBOX_FUNCTIE).optional(),
    userId: z.string().min(1).optional(),
    employeeId: z.string().min(1).optional(),
    notitie: z.string().max(500).optional(),
  })
  .refine(
    (v) =>
      [v.functie, v.userId, v.employeeId].filter((x) => Boolean(x)).length === 1,
    { message: 'Geef precies één van functie, userId of employeeId op' },
  )

// Spec 19 §5.2.E — goedkeuren (reden optioneel) / afkeuren (reden verplicht).
export const inboxItemGoedkeurenSchema = z.object({
  reden: z.string().max(500).optional(),
})

export const inboxItemAfkeurenSchema = z.object({
  reden: z.string().min(1, 'Reden is verplicht bij afkeuren').max(500),
})

export const userInboxFunctieUpsertSchema = z.object({
  // idParam i.p.v. .cuid(): 81% van de prod-users heeft een legacy-UUID-id.
  userId: idParam('userId'),
  functies: z.array(z.enum(INBOX_FUNCTIE)),
})

export type InboxItemFilterInput = z.infer<typeof inboxItemFilterSchema>
export type InboxItemClaimInput = z.infer<typeof inboxItemClaimSchema>
export type InboxItemAfhandelInput = z.infer<typeof inboxItemAfhandelSchema>
export type InboxItemDoorsturenInput = z.infer<typeof inboxItemDoorsturenSchema>
export type InboxItemGoedkeurenInput = z.infer<typeof inboxItemGoedkeurenSchema>
export type InboxItemAfkeurenInput = z.infer<typeof inboxItemAfkeurenSchema>
export type UserInboxFunctieUpsertInput = z.infer<typeof userInboxFunctieUpsertSchema>

// ─── FO-107 — per-type postvak-schakelaar (admin) ────────────────────────────

export const POSTVAK_EVENT_MODUS = ['ITEM', 'STIL', 'UIT'] as const
export type PostvakEventModus = (typeof POSTVAK_EVENT_MODUS)[number]

export const POSTVAK_EVENT_MODUS_LABELS: Record<PostvakEventModus, string> = {
  ITEM: 'Postvak-item',
  STIL: 'Stil (geen melding-badge)',
  UIT: 'Uit (geen postvak-item)',
}

/**
 * Per event-type de postvak-routing + de afgaat-frequentie (ter beoordeling van
 * het nut, FO-107 §8/§10). `recentCount` = aantal inbox-items van dit type in het
 * `recentWindowDays`-venster.
 */
export interface PostvakEventConfigDTO {
  eventTypeKey: string
  postvakModus: PostvakEventModus
  prioriteitOverride: InboxItemPrioriteit | null
  isActief: boolean
  recentCount: number
  recentWindowDays: number
  /**
   * batch-67 meldingen-ontvangers (FO-500) - staat het gedeelde rolpostvak-item
   * (functie-fan-out) voor dit event aan? De EFFECTIEVE waarde: wat beheer koos,
   * anders de standaard (uit voor factuur- en inkoopmeldingen, anders aan). De
   * persoonlijke werkvoorraad-items staan hier los van.
   */
  rolpostvak: boolean
  /** True = beheer heeft niets gekozen; `rolpostvak` is dan de standaard. */
  rolpostvakStandaard: boolean
  /** True wanneer er een expliciete config-rij is (anders toont de lijst de default-flip). */
  expliciet: boolean
  /**
   * batch-67 - de functie-inboxen waar het gedeelde item zou landen (de
   * functie-mapping van het event, anders de standaard van zijn doelgroep). Leeg
   * = dit meldingstype is aan geen functie-inbox gekoppeld; de rolpostvak-keuze
   * heeft dan geen effect en het scherm biedt hem niet aan.
   */
  functies: InboxFunctie[]
}

export const postvakEventConfigUpdateSchema = z.object({
  eventTypeKey: z.string().min(1),
  postvakModus: z.enum(POSTVAK_EVENT_MODUS),
  prioriteitOverride: z.enum(INBOX_ITEM_PRIORITEIT).nullable().optional(),
  isActief: z.boolean().optional(),
  // batch-67 - weggelaten = de huidige keuze behouden; `null` = terug naar de
  // standaard (uit voor factuur- en inkoopmeldingen, anders aan).
  rolpostvak: z.boolean().nullable().optional(),
})
export type PostvakEventConfigUpdateInput = z.infer<typeof postvakEventConfigUpdateSchema>
